import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { EVENT_TYPES } from '@/shared/constants/event-types.v4';
import { Box } from '@/shared/design-system/primitives';

// Atomic Components
import { ComposerHeader } from './components/atomic/ComposerHeader';
import { ComposerFooter } from './components/atomic/ComposerFooter';
import { EventTypeSelector } from './components/atomic/EventTypeSelector';
import { StatusSelector } from './components/atomic/StatusSelector';
import { TemplateSelector } from './components/atomic/TemplateSelector';
import { UnifiedTemporalPicker } from './components/atomic/UnifiedTemporalPicker';

// Components
import { EventComposerBodySwitcher } from './components/EventComposerBodySwitcher';
import { EventComposerLoadingView } from './components/EventComposerLoadingView';
import { TimeInputSystem } from './components/TimeInputSystem';

// Hooks
import { useEventComposer } from './hooks/useEventComposer';
import { useTimeInputController } from './hooks/useTimeInputController';
import { formatDisplayDate } from '@/utils/date-utils';

// Actions
import { ComposerActions } from './components/atomic/ComposerActions';

interface EventComposerProps {
    onNavigate: (view: string) => void;
}

/**
 * EventComposer: The polymorphic entry point for all system events.
 * V6.0: LIFTED MODAL STATE. Logic decomposed into BodySwitcher, TimeInputSystem, and LoadingView.
 * Hardened Z-index by rendering the modal at the root level of the shell.
 */
export const EventComposer: React.FC<EventComposerProps> = ({ onNavigate }) => {
    const {
        composedEvent,
        isLoading,
        programs,
        programDays,
        selectedProgramId,
        setSelectedProgramId,
        templateDetails,
        localTitle,
        setLocalTitle,
        timeIntervals,
        isPastDate,
        closeComposer,
        updateComposedEvent,
        updatePreviewData,
        saveComposedEvent,
        deleteComposedEvent,
        startSessionFromComposer,
        updatePerformanceLog,
        handleProgramDaySelect,
        handleTypeChange,
        handleUpdatePayload
    } = useEventComposer();

    // Lifted Modal Logic
    const { 
        activeTimePickerModal, 
        setActiveTimePickerModal, 
        currentDuration, 
        commitTimeChange 
    } = useTimeInputController({ composedEvent, updateComposedEvent });

    useEffect(() => {
        console.log("[EventComposer] Mounted", { hasEvent: !!composedEvent });
    }, [composedEvent]);

    // VIEWPORT CONSTRAINED STYLE (Centered fixed container)
    const containerStyle: React.CSSProperties = {
        position: 'fixed',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        width: '100%',
        maxWidth: '500px',
        margin: '0 auto',
        backgroundColor: '#000',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 0 100px rgba(0,0,0,0.8)'
    };

    // --- LOADING STATE ---
    if (!composedEvent) {
        return <EventComposerLoadingView containerStyle={containerStyle} />;
    }

    return (
        <div style={containerStyle} className="animate-in fade-in zoom-in-95 duration-200">
            <ComposerHeader 
                isExisting={!!composedEvent.id}
                localTitle={localTitle}
                setLocalTitle={setLocalTitle}
                onBlur={() => updateComposedEvent({ title: localTitle })}
                onClose={closeComposer}
                onDelete={deleteComposedEvent}
                onSave={saveComposedEvent}
                isLoading={isLoading}
            />

            <div className="scrollable-content" style={{ paddingBottom: '120px' }}>
                {/* 1. DOMAIN CONFIGURATION */}
                <EventTypeSelector
                    currentType={composedEvent.type}
                    onTypeChange={handleTypeChange}
                />
                <Box style={{ marginTop: 4 }}>
                    <StatusSelector 
                        currentStatus={composedEvent.status} 
                        onStatusChange={(status) => updateComposedEvent({ status })} 
                    />
                </Box>

                {/* 2. TEMPORAL BOUNDS (Unified System) */}
                <Box style={{ marginTop: 4 }}>
                    <TimeInputSystem 
                        composedEvent={composedEvent}
                        updateComposedEvent={updateComposedEvent}
                        timeIntervals={timeIntervals}
                        formatDisplayDate={formatDisplayDate}
                        activeTimePickerModal={activeTimePickerModal}
                        setActiveTimePickerModal={setActiveTimePickerModal}
                    />
                </Box>

                {/* 3. PROGRAM BRIDGE (For Training & Mobility) */}
                {(composedEvent.type === EVENT_TYPES.PERFORMANCE || composedEvent.type === EVENT_TYPES.MOBILITY) && (
                    <Box style={{ marginTop: 4 }}>
                        <TemplateSelector 
                            programs={programs}
                            selectedProgramId={selectedProgramId}
                            onProgramChange={setSelectedProgramId}
                            programDays={programDays}
                            selectedDayId={composedEvent.programDayId}
                            onDayChange={handleProgramDaySelect}
                            isLoading={isLoading}
                        />
                    </Box>
                )}

                {/* 4. DYNAMIC BODY */}
                <div style={{ padding: '20px 15px', marginTop: 4 }}>
                    <EventComposerBodySwitcher 
                        event={composedEvent}
                        templateDetails={templateDetails}
                        updatePerformanceLog={updatePerformanceLog}
                        updatePreviewData={updatePreviewData}
                        updatePayload={handleUpdatePayload}
                        onStartTracker={() => startSessionFromComposer(onNavigate)}
                    />
                </div>

                {/* 5. TACTICAL ACTIONS */}
                {composedEvent.id && (
                    <ComposerActions event={composedEvent} />
                )}

                {/* 6. COMPOSER FOOTER (Now unpinned) */}
                <Box style={{ padding: '0 15px 40px' }}>
                    <ComposerFooter 
                        status={composedEvent.status}
                        type={composedEvent.type}
                        programDayId={composedEvent.programDayId}
                        title={composedEvent.title}
                        isLoading={isLoading}
                        isPastDate={isPastDate}
                        onStart={() => startSessionFromComposer(onNavigate)}
                        onSave={saveComposedEvent}
                    />
                </Box>
            </div>

            {/* Z-INDEX HARDENED PICKER (Portaled to Body) */}
            {activeTimePickerModal && ReactDOM.createPortal(
                <div style={{ 
                    position: 'fixed', 
                    inset: 0, 
                    zIndex: 100001, 
                    backgroundColor: '#000', // 100% Opaque Black
                    display: 'flex',
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    padding: '20px'
                }}>
                    <UnifiedTemporalPicker 
                        label={activeTimePickerModal === 'START' ? 'SELECT START' : (activeTimePickerModal === 'END' ? 'SELECT END' : 'SET DURATION')}
                        mode={activeTimePickerModal}
                        date={activeTimePickerModal === 'START' ? composedEvent.startDate : composedEvent.endDate}
                        time={activeTimePickerModal === 'START' ? composedEvent.startTime : composedEvent.endTime}
                        duration={currentDuration}
                        onUpdate={commitTimeChange}
                        onClose={() => setActiveTimePickerModal(null)}
                    />
                </div>,
                document.body
            )}
        </div>
    );
};
